import React from 'react';
import { LegalShell, Section, List } from '../../components/legal/LegalShell';

const VulnerableCustomerPolicy = () => (
  <LegalShell eyebrow="Legal" title="Vulnerable Customer Policy" updated="07 August 2026">
    <p>Domestic Home Services Limited is committed to treating every customer with care, fairness, respect and dignity.</p>
    <p>We recognise that some customers may experience circumstances that make it more difficult for them to understand information, make decisions, communicate with us, manage payments or access our services.</p>
    <p>This policy explains how we identify and support customers who may require additional assistance when dealing with Domestic Home Services Limited</p>

    <Section n="1" title="What We Mean by Vulnerability">
      <p>A customer may be considered vulnerable where their personal circumstances mean they could be at greater risk of experiencing difficulty or disadvantage when using our services.</p>
      <p>Vulnerability may be temporary, ongoing or may change over time.</p>
      <p>Examples may include circumstances relating to:</p>
      <p><b>Health</b></p>
      <List items={[
        'Physical illness.',
        'Disability.',
        'Mental health difficulties.',
        'Cognitive impairment.',
        'Hearing or visual impairment.',
        'Serious or long-term medical conditions.',
      ]} />
      <p><b>Life Events</b></p>
      <List items={[
        'Bereavement.',
        'Relationship breakdown.',
        'Loss of employment.',
        'Significant changes in household circumstances.',
        'Caring responsibilities.',
        'Serious illness within the family.',
      ]} />
      <p><b>Financial Resilience</b></p>
      <List items={[
        'Financial hardship.',
        'Unexpected reduction in income.',
        'Difficulty meeting regular financial commitments.',
        'Significant unexpected household expenses.',
      ]} />
      <p><b>Communication or Understanding</b></p>
      <List items={[
        'Difficulty reading or understanding written information.',
        'Limited English language ability.',
        'Limited digital skills.',
        'Difficulty using online services.',
        'Learning difficulties.',
        'Difficulty understanding financial or contractual information.',
      ]} />
      <p>These examples are not exhaustive.</p>
    </Section>

    <Section n="2" title="Our Commitment">
      <p>Domestic Home Services Limited is committed to providing appropriate support where we become aware that a customer may require additional assistance.</p>
      <p>Our team will aim to:</p>
      <List items={[
        'Listen carefully to customers.',
        'Treat customers with patience and respect.',
        'Avoid making assumptions about an individual\'s circumstances.',
        'Explain information clearly and in straightforward language.',
        'Allow additional time where required.',
        'Provide reasonable communication adjustments.',
        'Help customers understand their maintenance service plan.',
        'Support customers when discussing repairs, payments, cancellations or complaints.',
        'Respect the customer\'s privacy and confidentiality.',
      ]} />
      <p>We will never knowingly disadvantage a customer because they require additional support.</p>
    </Section>

    <Section n="3" title="Identifying Customers Who May Need Support">
      <p>Customers are not required to describe themselves as vulnerable.</p>
      <p>Our team may identify that additional support could be helpful based on information voluntarily provided by a customer or through the way a customer communicates with us.</p>
      <p>Examples may include a customer:</p>
      <List items={[
        'Advising us that they have a disability or health condition.',
        'Experiencing bereavement or another significant life event.',
        'Having difficulty understanding information.',
        'Asking for information to be repeated or explained differently.',
        'Experiencing financial hardship.',
        'Requiring assistance from a family member, carer or representative.',
        'Having difficulty communicating through telephone, email or online services.',
      ]} />
      <p>Where appropriate, our team may ask whether there is anything we can do to make communication easier.</p>
    </Section>

    <Section n="4" title="How to Tell Us You Need Additional Support">
      <p>You can tell us at any time if there is anything we can do to make it easier for you to communicate with us or use our services.</p>
      <p>You do not need to provide more personal information than is reasonably necessary.</p>
      <p>Please contact us:</p>
      <p>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>Where appropriate and with your agreement, we may make a note on your customer account so that you do not need to repeatedly explain your circumstances.</p>
    </Section>

    <Section n="5" title="Communication Adjustments">
      <p>Where reasonably possible, we will adapt our communication methods to meet a customer's individual needs.</p>
      <p>This may include:</p>
      <List items={[
        'Providing additional time to understand information.',
        'Explaining information in simpler language.',
        'Repeating or clarifying information where necessary.',
        'Communicating through email instead of telephone.',
        'Communicating by telephone instead of email.',
        'Providing written confirmation of important information.',
        'Allowing an authorised family member, carer or representative to assist.',
        'Providing documents in a larger-print format where reasonably practicable.',
        'Considering other reasonable accessibility requests.',
      ]} />
      <p>If you require a particular adjustment, please contact us and we will do our best to accommodate your request.</p>
    </Section>

    <Section n="6" title="Supporting Customers During Sales">
      <p>We want customers to understand the maintenance service plan they are purchasing.</p>
      <p>Where a customer appears to require additional support, we may:</p>
      <List items={[
        'Slow down the conversation.',
        'Repeat important information.',
        'Explain the plan in simpler terms.',
        'Give the customer additional time to consider the plan.',
        'Encourage the customer to review the Terms & Conditions.',
        'Provide information in writing.',
        'Allow the customer to discuss the plan with a trusted family member or representative.',
      ]} />
      <p>We will not knowingly pressure a customer into purchasing a plan where we believe they do not understand the product or the commitment they are making.</p>
    </Section>

    <Section n="7" title="Financial Difficulty">
      <p>We understand that customers' financial circumstances can change.</p>
      <p>If you are experiencing difficulty making a payment, please contact us as soon as possible.</p>
      <p>Depending on the circumstances, we may be able to discuss reasonable options regarding your maintenance service plan.</p>
      <p>This may include:</p>
      <List items={[
        'Discussing outstanding payments.',
        'Considering an alternative payment arrangement where appropriate.',
        'Explaining cancellation options.',
        'Providing additional time in appropriate circumstances.',
        'Signposting customers to independent organisations that may be able to provide financial guidance.',
      ]} />
      <p>Any arrangement will depend on the individual circumstances and the terms of the customer's maintenance service plan.</p>
    </Section>

    <Section n="8" title="Authorised Representatives">
      <p>A customer may ask a trusted person to assist them when communicating with us.</p>
      <p>This may include:</p>
      <List items={[
        'A family member.',
        'Carer.',
        'Friend.',
        'Attorney.',
        'Other authorised representative.',
      ]} />
      <p>Before discussing personal information with another person, we may need to confirm the customer's authority or consent.</p>
      <p>For temporary assistance, verbal or written consent may be accepted where appropriate.</p>
      <p>For formal arrangements such as a Power of Attorney, we may request appropriate documentation before allowing the representative to fully manage the customer's account.</p>
    </Section>

    <Section n="9" title="Customers with Power of Attorney">
      <p>Where a valid Power of Attorney or equivalent legal authority exists, we will take reasonable steps to verify the documentation before discussing or making changes to a customer's account.</p>
      <p>Once verified, the authorised representative may communicate with us in accordance with the authority granted to them.</p>
      <p>We reserve the right to request additional verification where necessary to protect the customer against fraud or unauthorised access.</p>
    </Section>

    <Section n="10" title="Privacy and Confidentiality">
      <p>Information relating to a customer's personal circumstances will be treated with care and confidentiality.</p>
      <p>We will only record information that is reasonably necessary to provide appropriate support or manage the customer's account.</p>
      <p>Access to this information will be restricted to employees or service providers who reasonably require it for their role.</p>
      <p>Personal information is handled in accordance with applicable UK data protection legislation and our Privacy Policy.</p>
    </Section>

    <Section n="11" title="Supporting Customers During Repairs">
      <p>Where a customer requires additional assistance during a repair or maintenance request, we will take reasonable steps to help.</p>
      <p>This may include:</p>
      <List items={[
        'Providing clearer appointment information.',
        'Communicating with an authorised representative.',
        'Requesting that contractors take account of agreed accessibility requirements.',
        'Providing additional explanation about the repair process.',
        'Helping customers understand what happens next.',
      ]} />
      <p>Where contractors are informed of additional support requirements, we will only share information reasonably necessary for providing the service.</p>
    </Section>

    <Section n="12" title="Complaints from Vulnerable Customers">
      <p>Customers requiring additional support have the same right to make a complaint as any other customer.</p>
      <p>Where additional assistance is required, we will make reasonable adjustments to help the customer raise and understand their complaint.</p>
      <p>Complaints will be:</p>
      <List items={[
        'Recorded.',
        'Investigated fairly.',
        'Reviewed objectively.',
        'Responded to clearly.',
        'Used to identify opportunities for service improvement.',
      ]} />
      <p>Customers may also ask an authorised representative to assist them with a complaint.</p>
    </Section>

    <Section n="13" title="Staff Awareness and Training">
      <p>Customer-facing employees are expected to understand the importance of recognising when additional customer support may be required.</p>
      <p>Relevant employees receive guidance and training covering areas such as:</p>
      <List items={[
        'Recognising potential vulnerability.',
        'Communicating sensitively.',
        'Avoiding assumptions.',
        'Providing reasonable adjustments.',
        'Protecting confidential information.',
        'Escalating concerns appropriately.',
      ]} />
      <p>Training and internal procedures are reviewed periodically.</p>
    </Section>

    <Section n="14" title="Working with Third-Party Contractors">
      <p>Where independent contractors interact directly with our customers, we expect them to treat customers professionally, respectfully and with appropriate consideration for individual circumstances.</p>
      <p>Where necessary and lawful, relevant accessibility or support requirements may be communicated to a contractor so that they can provide the service appropriately.</p>
    </Section>

    <Section n="15" title="Monitoring and Improvement">
      <p>We regularly review customer feedback, complaints and service outcomes to identify areas where additional customer support may be required.</p>
      <p>Where recurring concerns are identified, we may review our:</p>
      <List items={[
        'Customer communications.',
        'Staff training.',
        'Sales procedures.',
        'Repair processes.',
        'Complaint handling.',
        'Accessibility arrangements.',
      ]} />
      <p>Our objective is to continually improve the experience of all customers, particularly those who may require additional support.</p>
    </Section>

    <Section n="16" title="Contact Us">
      <p>If you need additional support, wish to tell us about your circumstances or have any questions regarding this policy, please contact:</p>
      <p><b>Domestic Home Services Limited</b><br/>Registered Office:<br/>128 City Road<br/>London<br/>United Kingdom<br/>EC1V 2NX</p>
      <p>Company Number: 17368184<br/>Telephone: +44 (0)113 548 7557<br/>Email: hello@domestichomeservices.com</p>
      <p>Domestic Home Services Limited reviews this policy periodically to ensure that it continues to support our commitment to providing fair, respectful and accessible services to all customers.</p>
    </Section>
  </LegalShell>
);

export default VulnerableCustomerPolicy;
